/**
 * Tapfiliate client helpers.
 *
 * The Tapfiliate pixel (loaded by src/util/includeScripts.js when
 * REACT_APP_TAPFILIATE_ACCOUNT_ID is set) detects inbound ?ref= referral clicks.
 * In addition, we capture the ?ref= referral code OURSELVES into a first-party
 * cookie here, because:
 *   - Tapfiliate does not document the name of the cookie its pixel sets, and
 *   - conversions are tracked SERVER-SIDE (from the transaction poller) after
 *     payment is confirmed, so the backend needs a durable referral value that
 *     we control and can thread through checkout.
 *
 * All exports are no-ops on the server (no window/document). Wrapped in
 * try/catch so a tracking failure can never break the app.
 */

const REF_COOKIE_NAME = 'tapRef';
// Timestamp (ISO) of when the ?ref= link was clicked — used server-side to judge
// whether the buyer was a new/lapsed customer at click time.
const REF_AT_COOKIE_NAME = 'tapRefAt';
// The buyer's lastActivity as it was BEFORE this visit's login overwrote it.
// Captured at the login/signup moment so a returning lapsed user can be detected.
const REF_PREV_ACTIVITY_COOKIE_NAME = 'tapRefPrevActivity';
// Sentinel for "we looked but there was no prior activity" (brand-new signup),
// so we can distinguish it from "never captured".
const REF_PREV_ACTIVITY_NONE = 'none';
// Attribution window — keep in sync with the cookie lifetime configured in the
// Tapfiliate program settings.
const REF_COOKIE_MAX_AGE_DAYS = 90;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const readCookie = name => {
  if (!isBrowser()) {
    return null;
  }
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
};

const writeCookie = (name, value, maxAgeDays) => {
  if (!isBrowser()) {
    return;
  }
  try {
    const maxAgeSeconds = Math.round(maxAgeDays * 24 * 60 * 60);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch (e) {
    // Never let tracking break the app.
  }
};

/**
 * If the current URL carries a ?ref= referral code, persist it to the tapRef
 * cookie so it survives until checkout. First-touch wins: we don't overwrite an
 * existing referral (matches typical affiliate attribution). Call once on app
 * load (client-side).
 */
export const captureAffiliateRef = () => {
  if (!isBrowser()) {
    return;
  }
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref && !readCookie(REF_COOKIE_NAME)) {
      writeCookie(REF_COOKIE_NAME, ref, REF_COOKIE_MAX_AGE_DAYS);
      // Record the click time alongside the code (same first-touch lifetime).
      writeCookie(REF_AT_COOKIE_NAME, new Date().toISOString(), REF_COOKIE_MAX_AGE_DAYS);
    }
  } catch (e) {
    // Never let tracking break the app.
  }
};

/**
 * Snapshot the user's PRIOR lastActivity for affiliate eligibility. Must be
 * called at the login/signup moment BEFORE the new lastActivity is written, so
 * that a returning lapsed user's old activity is captured before it's overwritten.
 *
 * Only records when an affiliate referral is pending (tapRef cookie present) and
 * we haven't already snapshotted for this referral. A brand-new signup has no
 * prior activity → records the "none" sentinel (treated as new = payable).
 * No-op when there's no pending referral.
 */
export const recordReferralPriorActivity = currentUser => {
  if (!isBrowser()) {
    return;
  }
  try {
    if (!readCookie(REF_COOKIE_NAME) || readCookie(REF_PREV_ACTIVITY_COOKIE_NAME)) {
      return;
    }
    const priorActivity = currentUser?.attributes?.profile?.publicData?.lastActivity || null;
    writeCookie(
      REF_PREV_ACTIVITY_COOKIE_NAME,
      priorActivity || REF_PREV_ACTIVITY_NONE,
      REF_COOKIE_MAX_AGE_DAYS
    );
  } catch (e) {
    // Never let tracking break the app.
  }
};

/**
 * Return the captured affiliate referral code (or null). Read at checkout to
 * thread onto the transaction's protectedData, and at signup to tag the Klaviyo
 * profile.
 */
export const getAffiliateRef = () => readCookie(REF_COOKIE_NAME);

/** Return the ISO click timestamp for the pending referral (or null). */
export const getAffiliateRefAt = () => readCookie(REF_AT_COOKIE_NAME);

/**
 * Return the captured prior-activity snapshot. Returns null if never captured,
 * the "none" sentinel string if captured-but-empty (new signup), or the prior
 * lastActivity value otherwise.
 */
export const getAffiliateRefPrevActivity = () => readCookie(REF_PREV_ACTIVITY_COOKIE_NAME);

export const TAPFILIATE_REF_COOKIE_NAME = REF_COOKIE_NAME;
export const TAPFILIATE_REF_PREV_ACTIVITY_NONE = REF_PREV_ACTIVITY_NONE;
